<template>
	<section class="mt-4 max-h-[70vh] overflow-y-scroll px-0.5">
		<div class="flex flex-col items-center">
			<p
				class="text-secondary-900 mb-1 text-center text-base leading-normal font-bold"
			>
				Everplans Lifetime Payment Authorization
			</p>
			<p class="text-center text-sm font-normal text-slate-500">
				Please provide policy holder information below to confirm signup for
				Lifetime Everplans account.
			</p>
		</div>

		<div class="grid grid-cols-1 gap-4 *:w-full *:max-w-none">
			<FormKit
				type="dropdown"
				v-model="paymentType"
				@update:modelValue="setDefaultPaymentDetails"
				name="paymentType"
				:options="[
					{ label: t('document.eft'), value: PlanEverplansPaymentType.EFT },
					{
						label: t('document.creditCard'),
						value: PlanEverplansPaymentType.CreditCard,
					},
				]"
				:label="t('document.paymentType')"
				label-class="required mb-2"
				validation="required"
				inner-class="rounded-lg!"
				deselect="false"
			/>
		</div>

		<hr class="mt-5 mb-9 border-slate-200" />

		<FormKit
			#default="{ state: { valid } }"
			v-model="paymentDetailsModel"
			:key="`everplansPaymentDetails-${planId}`"
			type="form"
			:actions="false"
			form-class="w-full grow h-full shrink-0 relative"
		>
			<Transition
				name="fade"
				mode="out-in"
			>
				<div
					v-if="everplansPayment.paymentType === PlanEverplansPaymentType.EFT"
				>
					<EftFields
						field-account-holder
						field-bank-name
						show-check
						fullWidthCheck
						v-model="everplansPayment.paymentDetails"
					/>
					<div class="flex flex-col gap-3 border-t border-gray-100 pt-3">
						<RegularButton
							type="button"
							class="bg-blue-500 px-5 py-3 font-bold text-white hover:bg-blue-700"
							:disabled="!valid"
							@click="submit"
						>
							{{ t('home.everplansResubmit.authorizeAndResubmitToEverplans') }}
						</RegularButton>
						<RegularButton
							@click="emit('closeModal')"
							class="w-full justify-center rounded-md px-5 py-2 font-light text-slate-700 hover:text-slate-900"
						>
							{{ t('general.cancel') }}
						</RegularButton>
					</div>
				</div>
				<ResubmitStripeForm
					v-else
					@close-modal="emit('closeModal')"
					:plan-id="planId"
					:purchase-amount="purchasePrice"
					@token-success="setCreditCardPayment"
				/>
			</Transition>
		</FormKit>
	</section>
</template>

<script setup lang="ts">
import EftFields from '@/components/documents/EftFields.vue'
import RegularButton from '@/components/forms/RegularButton.vue'
import ResubmitStripeForm from '@/components/home/SubmissionStatuses/ResubmissionModals/ResubmitStripeForm.vue'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { getBlankEft } from '@/mixins/eft'
import { useMerchandise } from '@/mixins/merchandise'
import type {
	CreditCard,
	CreditCardEverplansPayment,
	EFT,
	EftEverplansPayment,
} from '@/types'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export type FixPaymentProps = { planId: string; purchaseAmount?: number }

const { t } = useI18n()

const props = defineProps<FixPaymentProps>()
const emit = defineEmits(['closeModal', 'paymentSubmitted'])

const { mockProductEverplans } = useMerchandise()

const defaultPrice = mockProductEverplans.value?.fixedPrice // 299

const purchasePrice = props.purchaseAmount || defaultPrice

const everplansPayment = ref<EftEverplansPayment | CreditCardEverplansPayment>({
	paymentType: PlanEverplansPaymentType.EFT,
	paymentDetails: getBlankEft(),
	amount: purchasePrice,
})

const paymentType = computed({
	get: () => everplansPayment.value.paymentType,
	set: (newPaymentType) => {
		everplansPayment.value.paymentType = newPaymentType
	},
})

const paymentDetailsModel = computed({
	get: () => everplansPayment.value.paymentDetails ?? {},
	set: (val) => {
		everplansPayment.value.paymentDetails = val as EFT | CreditCard
	},
})

const setDefaultPaymentDetails = () => {
	const currentPaymentType = paymentType.value
	if (currentPaymentType === PlanEverplansPaymentType.EFT) {
		paymentDetailsModel.value = getBlankEft()
	} else if (currentPaymentType === PlanEverplansPaymentType.CreditCard) {
		paymentDetailsModel.value = {
			token: '',
		}
	}
}

const setCreditCardPayment = (details: CreditCard) => {
	paymentDetailsModel.value = details
	// could immediately submit and close modal here.
	submit()
}

const submit = async () => {
	emit('paymentSubmitted', everplansPayment.value)
}
</script>

<style scoped></style>
